import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button, Flex, Typography } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { copyToClipboard } from '../../../utils/link.helper'
import { hexToRgba } from '../../../utils/colors.helper'
import { useThemeColors } from '../../../hooks/useThemeColors'
import HighlightScrollbar from './HighlightScrollbar'
import useHighlightNavigation from './useHighlightNavigation'

const { Paragraph, Text } = Typography

const buildSegments = (text, highlightRanges = [], excessRange = null, currentHighlightIndex = -1, showFocusedHighlight = false, colors = {}) => {
    const ranges = []
    const textLength = typeof text === 'string' ? text.length : 0
    if (Array.isArray(highlightRanges)) {
        highlightRanges.forEach((r, idx) => {
            if (r && typeof r.start === 'number' && typeof r.end === 'number' && r.end > r.start) {
                ranges.push({ start: Math.max(0, r.start), end: Math.min(textLength, r.end), type: 'hl', meta: r, idx })
            }
        })
    }
    if (excessRange) {
        ranges.push({ start: excessRange.start, end: excessRange.end, type: 'excess' })
    }

    // Build boundaries
    const boundaries = new Set([0, textLength])
    ranges.forEach(r => {
        boundaries.add(Math.max(0, Math.min(textLength, r.start)))
        boundaries.add(Math.max(0, Math.min(textLength, r.end)))
    })
    const points = Array.from(boundaries).sort((a, b) => a - b)

    // For each segment pick highest priority: highlight > excess > none
    const segments = []
    for (let i = 0; i < points.length - 1; i++) {
        const start = points[i]
        const end = points[i + 1]
        if (end <= start) continue
        const active = ranges.filter(r => r.start < end && r.end > start)
        let style = null
        let markIdx = null
        if (active.length) {
            const hl = active.filter(a => a.type === 'hl')
            const hasExcess = active.some(a => a.type === 'excess')
            if (hl.length) {
                // Prefer the last highlight in input order
                const chosen = hl[hl.length - 1]
                const defaultHighlightColor = colors.warning
                const { color = defaultHighlightColor, textColor, opacity = 1 } = chosen.meta || {}
                // Increase opacity for the currently focused highlight (only if user has navigated)
                const isFocused = showFocusedHighlight && chosen.idx === currentHighlightIndex
                const finalOpacity = isFocused ? 1 : opacity
                const bg = color.startsWith('#') ? hexToRgba(color, finalOpacity) : color
                style = { 
                    backgroundColor: bg, 
                    color: textColor || undefined,
                }
                markIdx = chosen.idx
            } else if (hasExcess) {
                style = {
                    textDecoration: 'line-through',
                    textDecorationColor: colors.error,
                    textDecorationThickness: '2px',
                }
            }
            // Ensure excess styling overlays any highlight styling if both apply
            if (hasExcess) {
                style = {
                    ...(style || {}),
                    textDecoration: 'line-through',
                    textDecorationColor: colors.error,
                    textDecorationThickness: '2px',
                }
            }
        }
        segments.push({ start, end, text: text.slice(start, end), style, markIdx })
    }
    return segments
}

const computeExcessRange = (text, wordsLimit) => {
    if (typeof wordsLimit !== 'number' || wordsLimit <= 0) return null
    if (!text) return null
    const tokens = text.trim().split(/\s+/).filter(Boolean)
    if (tokens.length <= wordsLimit) return null
    // Find char index where the (wordsLimit)th token ends
    let count = 0
    let index = 0
    const len = text.length
    while (index < len && count < wordsLimit) {
        // Skip leading spaces
        while (index < len && /\s/.test(text[index])) index++
        // Consume token
        while (index < len && !/\s/.test(text[index])) index++
        count++
    }
    return { start: index, end: len }
}

const SimpleTextViewer = ({
    className,
    title,
    value: inputValue,
    showActions = true,
    extraActions,
    wrapperStyles,
    showWordsCount = false,
    wordsLimit,
    highlightRanges,
    footer,
    maxHeight,
    fontSize = 16,
}) => {
    const value = typeof inputValue === 'string' ? inputValue.replace(/\u00A0/g, ' ') : inputValue || ''
    const scrollableRef = useRef(null)
    const colors = useThemeColors()

    const excessRange = useMemo(() => computeExcessRange(value, wordsLimit), [value, wordsLimit])

    // Anchors for highlight positioning (stable across renders)
    const highlightAnchorsRef = useRef([])
    useEffect(() => {
        const count = Array.isArray(highlightRanges) ? highlightRanges.length : 0
        const arr = highlightAnchorsRef.current || []
        for (let i = 0; i < count; i++) {
            if (!arr[i]) arr[i] = React.createRef()
        }
        arr.length = count
        highlightAnchorsRef.current = arr
    }, [highlightRanges])

    // Highlight navigation
    const navigation = useHighlightNavigation(scrollableRef, highlightRanges, highlightAnchorsRef)

    // Keyboard shortcuts for highlight navigation
    useEffect(() => {
        if (!navigation.showNavigation) return

        const handleKeyDown = (e) => {
            // Only handle if no input/textarea is focused
            const activeElement = document.activeElement
            const isInputFocused = activeElement && (
                activeElement.tagName === 'INPUT' || 
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.isContentEditable
            )
            
            if (isInputFocused) return

            switch (e.key) {
                case 'ArrowDown':
                case 'n':
                case 'N':
                    if (navigation.canGoNext) {
                        e.preventDefault()
                        navigation.goToNext()
                    }
                    break
                case 'ArrowUp':
                case 'p':
                case 'P':
                    if (navigation.canGoPrevious) {
                        e.preventDefault()
                        navigation.goToPrevious()
                    }
                    break
                default:
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [navigation])

    // Build segments with current highlight index for focused styling (only when user has navigated)
    const segments = useMemo(
        () => buildSegments(value, highlightRanges, excessRange, navigation.currentIndex, navigation.hasUserNavigated, colors),
        [value, highlightRanges, excessRange, navigation.currentIndex, navigation.hasUserNavigated, colors],
    )

    const wordsCount = useMemo(() => (value ? value.trim().split(/\s+/).filter(Boolean).length : 0), [value])
    const wordsLimitExceeded = typeof wordsLimit === 'number' && wordsCount > wordsLimit

    return (
        <div className={`editor-container ${className || ''}`}>
            <div className='editor-header'>
                <div className='editor-title'>{title}</div>
                {showActions && (
                    <div className='editor-actions'>
                        {Array.isArray(extraActions) &&
                            extraActions.map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)}
                        <Button
                            type='text'
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(value)}
                            title='Copy to Clipboard'
                        />
                    </div>
                )}
            </div>

            <div style={{ position: 'relative' }}>
                <div
                    ref={scrollableRef}
                    className='text-viewer-content'
                    style={{
                        borderRadius: 6,
                        padding: 12,
                        overflow: 'auto',
                        maxHeight: maxHeight,
                        fontSize,
                        lineHeight: 1.6,
                        backgroundColor: colors.nestedCardBg,
                        color: colors.text,
                        border: `1px solid ${colors.borderColor}`,
                        ...wrapperStyles,
                    }}
                >
                    <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                        {segments.map((seg, _idx) => {
                            const anchors = []
                            // Inject anchor(s) for any highlight whose start lies within this segment
                            if (Array.isArray(highlightRanges)) {
                                highlightRanges.forEach((r, i) => {
                                    if (r && typeof r.start === 'number' && r.start >= seg.start && r.start < seg.end) {
                                        anchors.push(
                                            <span key={`a-${i}-${seg.start}`} ref={highlightAnchorsRef.current[i]} />,
                                        )
                                    }
                                })
                            }
                            return (
                                <React.Fragment key={`${seg.start}-${seg.end}`}>
                                    {anchors}
                                    <span style={seg.style} data-test={seg.style ? 'text-highlight' : undefined}>{seg.text}</span>
                                </React.Fragment>
                            )
                        })}
                    </Paragraph>
                </div>
                <HighlightScrollbar
                    scrollableRef={scrollableRef}
                    highlightRanges={highlightRanges}
                    highlightAnchorsRef={highlightAnchorsRef}
                    onMarkerClick={navigation.scrollToHighlight}
                    currentIndex={navigation.hasUserNavigated ? navigation.currentIndex : -1}
                    colors={colors}
                />
            </div>
            <Flex justify={footer ? 'space-between' : 'end'} className='f-width' style={{ marginTop: footer ? 8 : 0 }}>
                {footer}
                {showWordsCount && (
                    <Text type='secondary' style={{ color: wordsLimitExceeded ? colors.error : undefined }} data-test='word-count-display'>
                        Words <span data-test='word-count-current'>{wordsCount}</span>
                        {wordsLimit ? <> / <span data-test='word-count-limit'>{wordsLimit}</span></> : ''} {wordsLimitExceeded ? '(limit exceeded)' : ''}
                    </Text>
                )}
            </Flex>
        </div>
    )
}

SimpleTextViewer.propTypes = {
    className: PropTypes.string,
    title: PropTypes.element,
    value: PropTypes.string,
    showActions: PropTypes.bool,
    extraActions: PropTypes.arrayOf(PropTypes.element),
    wrapperStyles: PropTypes.object,
    showWordsCount: PropTypes.bool,
    wordsLimit: PropTypes.number,
    highlightRanges: PropTypes.arrayOf(
        PropTypes.shape({
            start: PropTypes.number.isRequired,
            end: PropTypes.number.isRequired,
            color: PropTypes.string,
            textColor: PropTypes.string,
            opacity: PropTypes.number,
        }),
    ),
    footer: PropTypes.element,
    maxHeight: PropTypes.string,
    fontSize: PropTypes.number,
}

export default SimpleTextViewer
